import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ReviewCard, ReviewStats } from '../../components/ReviewCard';
import { getHostReviews, replyToReview } from '../../lib/api';
import { toast } from 'sonner';
import { Star, Loader2, MessageSquare, CheckCircle2 } from 'lucide-react';

export default function HostReviews() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadReviews();
  }, [activeTab]);

  const loadReviews = async (nextCursor = null) => {
    if (!nextCursor) setLoading(true);
    try {
      const params = { limit: 20, cursor: nextCursor };
      if (activeTab === 'needs_reply') params.needs_reply = true;
      if (activeTab === 'replied') params.needs_reply = false;
      
      const response = await getHostReviews(params);
      
      if (nextCursor) {
        setReviews(prev => [...prev, ...response.data.items]);
      } else {
        setReviews(response.data.items);
        setStats(response.data.stats);
      }
      setCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (reviewId, replyText) => {
    try {
      await replyToReview(reviewId, replyText);
      toast.success('Reply sent successfully');
      // Refresh reviews
      loadReviews();
    } catch (error) {
      console.error('Reply failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reply');
      throw error;
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setReviews([]);
    setCursor(null);
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="host-reviews">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Reviews</h1>
        <p className="text-slate-500 mt-1">
          Manage reviews for your properties
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.average_rating ? stats.average_rating.toFixed(1) : '-'}
                  </p>
                  <p className="text-sm text-slate-500">Average Rating</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_count}</p>
                  <p className="text-sm text-slate-500">Total Reviews</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  stats.needs_reply_count > 0 ? 'bg-amber-100' : 'bg-green-100'
                }`}>
                  <MessageSquare className={`w-6 h-6 ${
                    stats.needs_reply_count > 0 ? 'text-amber-500' : 'text-green-500'
                  }`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.needs_reply_count}</p>
                  <p className="text-sm text-slate-500">Awaiting Reply</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reviews List */}
      <Card>
        <CardHeader>
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="all">All Reviews</TabsTrigger>
              <TabsTrigger value="needs_reply">
                Needs Reply
                {stats?.needs_reply_count > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                    {stats.needs_reply_count}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="replied">Replied</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {reviews.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              {activeTab === 'all' && <p>No reviews yet.</p>}
              {activeTab === 'needs_reply' && <p>All caught up! No reviews awaiting your reply.</p>}
              {activeTab === 'replied' && <p>No replied reviews yet.</p>}
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <ReviewCard 
                  key={review.id} 
                  review={review} 
                  showProperty 
                  showReplyForm={!review.host_reply}
                  onReply={handleReply}
                />
              ))}
              
              {hasMore && (
                <div className="text-center pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => loadReviews(cursor)}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Load More
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
